import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Phone, Mail, MapPin, Clock } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { openCookiePreferences } from '../lib/consent';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const trimmed = email.trim();
    if (!trimmed) return;

    // Validação rápida de formato no cliente
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus({ type: 'error', message: 'Por favor insira um email válido.' });
      return;
    }

    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const res = await fetch(`${API_BASE}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed })
      });

      let data = {};
      try { data = await res.json(); } catch (_) { /* ignore */ }

      if (res.ok) {
        setStatus({ type: 'success', message: 'Subscrição confirmada. Obrigado!' });
        setEmail('');
      } else if (res.status === 422) {
        setStatus({ type: 'error', message: 'Por favor insira um email válido.' });
      } else {
        const detail = typeof data.detail === 'string' ? data.detail : 'Não foi possível subscrever. Tente novamente.';
        setStatus({ type: 'error', message: detail });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Erro de ligação. Tente novamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  const montserratLight = {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 300,
    letterSpacing: "0.01em"
  };

  const navLinks = [
    { to: '/ultimate', label: 'ULTIMATE' },
    { to: '/equipa', label: 'EQUIPA' },
    { to: '/personal-training', label: 'PERSONAL TRAINING' },
    { to: '/aulas', label: 'AULAS' },
    { to: '/contactos', label: 'CONTACTOS' }
  ];

  return (
    <footer className="bg-black text-white" data-testid="site-footer">
      {/* Contact Info Section */}
      <div className="bg-gray-100 text-black py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Contacto */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-white flex items-center justify-center shadow-xl border-2 border-black">
                  <Phone size={28} className="text-black" />
                </div>
              </div>
              <h3 className="mb-4 uppercase" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "26px", letterSpacing: "0.06em", lineHeight: "1" }}>CONTACTO</h3>
              <p className="text-sm mb-1" style={montserratLight}>T: {contactInfo.telefones.join(' / ')}</p>
              <p className="text-sm" style={montserratLight}>{contactInfo.email}</p>
            </div>

            {/* Horário */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-white flex items-center justify-center shadow-xl border-2 border-black">
                  <Clock size={28} className="text-black" />
                </div>
              </div>
              <h3 className="mb-4 uppercase" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "26px", letterSpacing: "0.06em", lineHeight: "1" }}>HORÁRIO</h3>
              <p className="text-sm mb-1" style={montserratLight}>{contactInfo.horario.semana}</p>
              <p className="text-sm" style={montserratLight}>{contactInfo.horario.sabado}</p>
            </div>

            {/* Localização */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-white flex items-center justify-center shadow-xl border-2 border-black">
                  <MapPin size={28} className="text-black" />
                </div>
              </div>
              <h3 className="mb-4 uppercase" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "26px", letterSpacing: "0.06em", lineHeight: "1" }}>LOCALIZAÇÃO</h3>
              <p className="text-sm" style={montserratLight}>{contactInfo.morada}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 items-start">
          {/* Logo & Socials */}
          <div>
            <div className="mb-6">
              <Link to="/">
                <img
                  src="/assets/logo-uf.png?v=2"
                  alt="Ultimate Fit"
                  className="h-12 w-auto object-contain"
                />
              </Link>
            </div>
            <div className="flex space-x-4">
              <a
                href={contactInfo.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 bg-white flex items-center justify-center text-black hover:bg-yellow-400 transition-colors"
              >
                <Instagram size={20} />
              </a>
              <a
                href={contactInfo.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 bg-white flex items-center justify-center text-black hover:bg-yellow-400 transition-colors"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://wa.me/351920627943?text=Olá!%20Gostaria%20de%20saber%20mais%20informações%20sobre%20o%20ULTIMATE%20FIT."
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                data-testid="footer-social-whatsapp"
                className="w-10 h-10 bg-white flex items-center justify-center text-black hover:bg-yellow-400 transition-colors"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.85.504 3.585 1.385 5.077L2 22l5.05-1.326A9.96 9.96 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 0 1-4.094-1.123l-.293-.174-3.001.788.801-2.926-.19-.301A7.96 7.96 0 0 1 4 12c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Navegação principal */}
          <div>
            <h3 className="mb-4 border-b-2 border-yellow-400 pb-2 inline-block uppercase" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "24px", letterSpacing: "0.06em", lineHeight: "1" }}>NAVEGAÇÃO</h3>
            <ul className="space-y-2 mt-4">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-nav-${link.to.replace('/', '')}`}
                    className="text-gray-400 hover:text-yellow-400 transition-colors"
                    style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "18px", letterSpacing: "0.04em" }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="mb-4 border-b-2 border-yellow-400 pb-2 inline-block uppercase" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "24px", letterSpacing: "0.06em", lineHeight: "1" }}>ACOMPANHA-NOS DE PERTO!</h3>
            <p className="text-gray-400 text-sm mb-4 mt-4" style={montserratLight}>Subscreva a nossa newsletter</p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Coloque aqui o seu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="newsletter-email-input"
                className="bg-transparent border-2 border-white text-white placeholder:text-gray-500 hover:border-yellow-400 focus:border-yellow-400 transition-colors"
                style={montserratLight}
              />
              <Button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit-btn"
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-2 border-yellow-400 hover:shadow-xl transform hover:-translate-y-0.5 transition-all text-[20px] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}
              >
                {submitting ? 'A SUBSCREVER...' : 'SUBSCREVER'}
              </Button>
              {status.message && (
                <p
                  data-testid="newsletter-status"
                  className={`text-sm pt-1 ${status.type === 'success' ? 'text-yellow-400' : 'text-red-400'}`}
                  style={montserratLight}
                >
                  {status.message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Bottom bar: legal + livro reclamações */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-gray-500 text-sm" style={montserratLight}>
              © {new Date().getFullYear()} Ultimate Fit. Todos os direitos reservados.
            </p>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <Link to="/politica-de-privacidade" data-testid="footer-link-privacidade" className="text-gray-400 hover:text-yellow-400 transition-colors" style={montserratLight}>
                Política de Privacidade
              </Link>
              <Link to="/termos-e-condicoes" data-testid="footer-link-termos" className="text-gray-400 hover:text-yellow-400 transition-colors" style={montserratLight}>
                Termos e Condições
              </Link>
              <Link to="/regulamento-interno" data-testid="footer-link-regulamento" className="text-gray-400 hover:text-yellow-400 transition-colors" style={montserratLight}>
                Regulamento Interno
              </Link>
              <button
                type="button"
                onClick={openCookiePreferences}
                data-testid="footer-link-cookies"
                className="text-gray-400 hover:text-yellow-400 transition-colors text-left"
                style={{ ...montserratLight, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', borderRadius: 0 }}
              >
                Definições de Cookies
              </button>

              {/* Livro de Reclamações */}
              <a
                href="https://www.livroreclamacoes.pt/Inicio/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-link-livro-reclamacoes"
                aria-label="Livro de Reclamações"
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-700 hover:border-yellow-400 transition-colors group"
              >
                <span className="block w-3 h-4 rounded-full bg-white group-hover:bg-yellow-400 transition-colors" aria-hidden="true" />
                <span className="text-white text-xs uppercase tracking-wider leading-tight" style={{ fontFamily: "'BebasNeue-Book', sans-serif" }}>
                  Livro de<br />Reclamações
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
